import type {
  ImportLedgerAccountsMutationOptions,
  ImportLedgerAccountsPayload,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysLedgerAccount } from '../queryKeys';

import { importLedgerAccounts } from './importLedgerAccounts';

/**
 * Imports or previews a complete chart-of-accounts replacement.
 *
 * Non-dry-run requests invalidate the ledger-account list after settling.
 *
 * @param options - Additional mutation options.
 * @returns The TanStack Query mutation state and import function.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts
 */
export const useMutationImportLedgerAccounts = (
  options?: ImportLedgerAccountsMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ImportLedgerAccountsPayload) =>
      importLedgerAccounts(payload),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      if (variables.isDryRun === false) {
        void queryClient.invalidateQueries({
          queryKey: queryKeysLedgerAccount.getAll(),
        });
      }

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
